import type { PickByValue } from 'utility-types'
import React, { type FC, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react'

import type { ReadonlyNonEmptyArray, ReadonlyRecord } from '../../../../utils'

import { type AppState, arePagesEquals, ConnectwareError, type Page, type SortableColumn, Translation } from '../../../../domain'

import type { PageSubscriptionArgs, SubscriptionFilterArgs, SubscriptionUpdateParameters, Usecases } from '../../../../application'

import { CircularLoader, Table, type TableColumn, type TableCustomizedState, type TableLine, type TablePagination, type TableProps } from '../../common'
import { ErrorMessage } from '../../ErrorMessage'
import { type AbsoluteRoutePathWithId, type RelativeRoutePathWithId, useRedirectionWithId } from '../../routing'
import { useAppState, useAppUsecase } from '../../State'
import { useSelection, type UseSelectionProps } from './Selection'

/** The columns can only have their display changed */
type DisplayChangingCoercion<TableResource,> = Partial<
    Readonly<{ [P in keyof TableResource]: Pick<TableColumn<TableResource[P], TableResource>, 'customCellRender' | 'label'> }>
>

/** This type forces the sorting to now be a boolean, and only if the original resource also contains that column */
type SortableCoercion<DataResource,> = Partial<Readonly<{ [P in SortableColumn<DataResource>]: Readonly<{ sort?: true }> }>>

export type Columns<DataResource, TableResource,> = DisplayChangingCoercion<TableResource> & SortableCoercion<DataResource>

type ManagementUsecase<DataResource,> = Readonly<{
    /** Used to subscribe to changes */
    subscribe(args: PageSubscriptionArgs): VoidFunction
    /** Used to update the pagination parameters */
    updateParameters(update: SubscriptionUpdateParameters<DataResource>): void
    getPaginationSizes(): ReadonlyNonEmptyArray<number>
    getResourcesSearchThreshold(): number
}>

type ResourceManagementUsecaseNames = keyof PickByValue<Usecases, ManagementUsecase<any>>

type ManagementUsecaseResource<U extends ManagementUsecase<any>,> = U extends ManagementUsecase<infer R> ? R : never
type ManagementUsecasesResource<U extends ResourceManagementUsecaseNames,> = ManagementUsecaseResource<Usecases[U]>

type Props<
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName>,
    TableResource extends TableLine,
    ManagementUsecases extends keyof Usecases,
> = Readonly<{
    /** The usecase the table will use to fetch data */
    subscriptionUsecase: SubscriptionUsecaseName

    /** Filter parameters for the resources to be fetched */
    filter?: SubscriptionFilterArgs

    /** How data should be provided for the table to be rendered */
    data: (s: AppState) => Page<DataResource>

    /**
     * Once errors/loading states have been filtered out
     * This function will be called to assist to creation of the contents of the table itself
     */
    dataTableMapper: (resource: readonly DataResource[]) => readonly TableResource[]

    /** The columns of the table */
    columns: Columns<DataResource, TableResource>

    /** If the list should be short */
    short?: boolean
}> &
    Pick<TableProps<TableResource>, 'onRowClick' | 'translations'> &
    UseSelectionProps<TableResource, ManagementUsecases>

const mapTableCustomizedStateToSubscriptionUpdateParameters = <TableResource, DataResource,>(
    s: TableCustomizedState<TableResource>
): SubscriptionUpdateParameters<DataResource> => {
    if ('search' in s) {
        /**
         * Search is nullable on SubscriptionUpdateParameters
         * @see {SubscriptionUpdateParameters['search']}
         */
        return { search: s.search || null }
    }

    if ('sortOrder' in s) {
        return {
            /**
             * This coercion is fine, as the type SortableCoercion above is only allowing sorting for column in data
             * @see {SortableCoercion}
             */
            sort: { column: s.sortOrder.name as SortableColumn<DataResource> & typeof s.sortOrder.name, asc: s.sortOrder.direction === 'asc' },
        }
    }

    /** These can be passed as is */
    return s
}

/**
 * This component has a bult-in structure to render a table that
 *
 * - While rendered, subscribes to the given usecase (if the same is subscribable)
 * - Handles state changes (loading, error, success yielf) of the given resources in order to properly render the contents
 * - Has a standardized look and feel
 *
 * Children will be rendered with-in the Table component
 */
export const ResourcesTable = <
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName>,
    TableResource extends TableLine = DataResource & TableLine,
    ManagementUsecases extends keyof Usecases = never,
>({
    subscriptionUsecase: subscriptionUsecaseName,
    filter = {},
    data: dataSelector,
    hasManagementCapabilitiesSelector,
    dataTableMapper,
    translations,
    selection,
    short = false,
    children,
    ...props
}: PropsWithChildren<Props<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>>): ReturnType<FC> => {
    const subscriptionUsecase = useAppUsecase(subscriptionUsecaseName) as ManagementUsecase<DataResource>

    const resourcesPage = useAppState<Page<DataResource>>(dataSelector, arePagesEquals)
    const selectionConfig = useSelection({ hasManagementCapabilitiesSelector, selection })

    /** Subscribe to changes of resources */
    useEffect(() => subscriptionUsecase.subscribe({ ...filter, short }), [subscriptionUsecase, filter.connection, filter.server, filter.service, short])

    /** Load the possible page sizes to be used */
    const [pageSizeOptions, searchThreshold] = useMemo(() => [subscriptionUsecase.getPaginationSizes(), subscriptionUsecase.getResourcesSearchThreshold()], [])

    const onCustomized = useCallback(
        (s: TableCustomizedState<TableResource>) => subscriptionUsecase.updateParameters(mapTableCustomizedStateToSubscriptionUpdateParameters(s)),
        [subscriptionUsecase]
    )

    if (resourcesPage === null) {
        /* Resources have not loaded */
        return <CircularLoader data-testid="no-data-loader" />
    }

    if (ConnectwareError.is(resourcesPage.data)) {
        return <ErrorMessage data-testid="general-error-message" error={resourcesPage.data} stack extras="section" />
    }

    const pagination: TablePagination | undefined = resourcesPage.data
        ? { ...resourcesPage.pagination, pageSizeOptions, totalCount: resourcesPage.data.totalCount, showTotalCount: true }
        : undefined

    return (
        <Table
            data-testid={subscriptionUsecaseName}
            selection={selectionConfig}
            search={resourcesPage.search === null || resourcesPage.search}
            searchThreshold={searchThreshold}
            data={resourcesPage.data === null ? [] : dataTableMapper(resourcesPage.data.current)}
            /**
             * If the data is loading, then check if searching and under threshold, then show custom message
             * Otherwise just show the spinner or simply no loader at all
             */
            loading={
                resourcesPage.data === null
                    ? resourcesPage.search !== null && resourcesPage.search.length < searchThreshold
                        ? Translation.SEARCH_THRESHOLD_EXPLANATION
                        : true
                    : false
            }
            pagination={pagination}
            sortOrder={{ name: resourcesPage.sort.column, direction: resourcesPage.sort.asc ? 'asc' : 'desc' }}
            translations={translations}
            onCustomized={onCustomized}
            sortData={false}
            {...props}
        >
            {children}
        </Table>
    )
}

type RedirectingProps<
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName>,
    TableResource extends TableLine,
    ManagementUsecases extends keyof Usecases,
> = Omit<Props<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>, 'onRowClick'> &
    Readonly<{ redirectOnRowclick: AbsoluteRoutePathWithId | RelativeRoutePathWithId }>

/**
 * The base resource needs an id for redirecting else where
 */
type RedirectingResource = TableLine & ReadonlyRecord<'id', string>

export const RedirectingResourcesTable = <
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName> & RedirectingResource = ManagementUsecasesResource<SubscriptionUsecaseName> &
        RedirectingResource,
    TableResource extends RedirectingResource = DataResource,
    ManagementUsecases extends keyof Usecases = never,
>({
    redirectOnRowclick,
    ...props
}: PropsWithChildren<RedirectingProps<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>>): ReturnType<FC> => {
    const onRowClick = useRedirectionWithId<TableResource>(redirectOnRowclick, ({ id }) => id)
    return onRowClick ? <ResourcesTable {...props} onRowClick={onRowClick} /> : <ResourcesTable {...props} />
}
